import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { billingApi, EntitlementsStatus, FeatureKey } from '../api/billing';
import { useAuth } from './AuthContext';

interface EntitlementsContextType {
  status: EntitlementsStatus | null;
  isLoading: boolean;
  /** True when the plan includes the feature (or billing is unconfigured). */
  hasFeature: (feature: FeatureKey) => boolean;
  refresh: () => Promise<void>;
}

const EntitlementsContext = createContext<EntitlementsContextType | undefined>(undefined);

export const useEntitlements = (): EntitlementsContextType => {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) {
    throw new Error('useEntitlements must be used within an EntitlementsProvider');
  }
  return ctx;
};

export const EntitlementsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState<EntitlementsStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setStatus(null);
      return;
    }
    setIsLoading(true);
    try {
      const data = await billingApi.getStatus();
      setStatus(data);
    } catch (error) {
      // Fail open in the UI: if status can't load, don't lock the user out.
      console.warn('Failed to load entitlements:', error);
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hasFeature = useCallback(
    (feature: FeatureKey): boolean => {
      // No status (loading/unconfigured/error) => don't gate.
      if (!status || !status.billingConfigured) return true;
      return status.features.includes(feature);
    },
    [status],
  );

  return (
    <EntitlementsContext.Provider value={{ status, isLoading, hasFeature, refresh }}>
      {children}
    </EntitlementsContext.Provider>
  );
};
